import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  MenuItem,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useContext, useEffect, useState } from "react";
import {
  DatePicker,
  DesktopTimePicker,
  LocalizationProvider,
} from "@mui/x-date-pickers";
import { useFormik } from "formik";
import * as Yup from "yup";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { Close, Delete, Edit } from "@mui/icons-material";
import SelectManufacturingOrder from "./SelectManufacturingOrder";

import dayjs from "dayjs";
import utc from "dayjs/plugin/utc"; // ES 2015
import timezone from "dayjs/plugin/timezone"; // ES 2015
import isoWeek from "dayjs/plugin/isoWeek"; // ES 2015
import SelectMultipleShopEmployees from "./SelectMultipleShopEmployees";
import SelectEventOrder from "./SelectEventOrder";
import SelectEventPhases from "./SelectEventPhases";
import SelectWorkStation from "./SelectWorkStation";
import { AuthContext } from "../../../../context/AuthContext";
import SelectEventRFLTasks from "./SelectEventRFLTasks";
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(isoWeek);
const DEFAULT_TIMEZONE = "America/Phoenix";
dayjs.tz.setDefault(DEFAULT_TIMEZONE);

const ScheduleJobDialog = ({
  dialogData,
  handleClose,
  handleSubmit,
  errorMessage,
}) => {
  const theme = useTheme();
  const { authToken, ezorder, decoded } = useContext(AuthContext);
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const validationSchema = Yup.object({
    eventEntity: Yup.mixed()
      .oneOf(["IND", "RFL"], "Entity required")
      .required("Entity required"),
    date: Yup.mixed().test(
      "is-valid-date",
      "Please select a valid date",
      (value) => dayjs.isDayjs(value) && value.isValid()
    ),
    startDate: Yup.mixed().test(
      "is-valid-date",
      "Please select a valid start time",
      (value) => dayjs.isDayjs(value) && value.isValid()
    ),
    endDate: Yup.mixed().test(
      "is-valid-date",
      "Please select a valid stop time",
      (value) => dayjs.isDayjs(value) && value.isValid()
    ),
    order: Yup.object()
      .nullable()
      .when("eventEntity", {
        is: "IND", // alternatively: (val) => val == true
        then: Yup.object()
          .nullable()
          .shape({
            id: Yup.number().required("Select an Industry Job"),
          })
          .required("Select an Industry Job"),
        otherwise: Yup.object().nullable(),
      }),
    manufacturingOrderId: Yup.number("Select a mfg")
      .nullable()
      .when("eventEntity", {
        is: "RFL", // alternatively: (val) => val == true
        then: Yup.number().nullable().required("Select an RFL Job"),
        otherwise: Yup.number().nullable(),
      }),
    workStation: Yup.object().nullable().shape({
      id: Yup.number().nullable(),
    }),
    assignedTo: Yup.array().of(
      Yup.object().shape({
        id: Yup.number(),
      })
    ),
    // .min(1, `Assign at least one person`),
    subPhases: Yup.array().of(
      Yup.object().shape({
        id: Yup.number(),
      })
    ),
    rflTasks: Yup.array().of(
      Yup.object().shape({
        transactionId: Yup.number(),
      })
    ),
  });

  const formik = useFormik({
    enableReinitialize: true,
    validateOnBlur: false,
    validateOnChange: true,
    initialValues: {
      id:
        dialogData.calendarEvent && dialogData.calendarEvent.id
          ? dialogData.calendarEvent.id
          : null,
      eventEntity: dialogData.calendarEvent
        ? dialogData.calendarEvent.eventEntity
        : "",
      date: dialogData.calendarEvent ? dialogData.calendarEvent.date : null,
      startDate: dialogData.calendarEvent
        ? dialogData.calendarEvent.startDate
        : null,
      endDate: dialogData.calendarEvent
        ? dialogData.calendarEvent.endDate
        : null,
      order: dialogData.calendarEvent ? dialogData.calendarEvent.order : null,
      manufacturingOrderId: dialogData.calendarEvent
        ? dialogData.calendarEvent.manufacturingOrderId
        : null,
      workStation: dialogData.calendarEvent
        ? dialogData.calendarEvent.workStation
        : null,
      assignedTo: dialogData.calendarEvent
        ? dialogData.calendarEvent.assignedTo
        : [],
      subPhases: dialogData.calendarEvent
        ? dialogData.calendarEvent.subPhases
        : [],
      rflTasks:
        dialogData.calendarEvent && dialogData.calendarEvent.rflTasks
          ? dialogData.calendarEvent.rflTasks
          : [],
    },
    validationSchema: validationSchema,
    onSubmit: (values) => {
      // Submit
      console.log("SUBMIT", values);
      handleSubmit(values);
    },
  });
  if (formik && formik.values)
    return (
      <Dialog
        open={dialogData.open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth={true}
      >
        <DialogTitle
          sx={{
            display: "flex",
            flexDirection: "row",
            alignItems: "flex-start",
          }}
        >
          <Typography
            sx={{
              flex: 1,
              fontFamily: "inherit",
              fontWeight: "inherit",
              fontSize: "inherit",
            }}
          >
            {dialogData.isRescheduling
              ? "Re-schedule Unfinished Tasks"
              : "Schedule a Job"}
          </Typography>
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "flex-start",
            }}
          >
            <IconButton onClick={handleClose} sx={{}}>
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>

        {dialogData.open && (
          <DialogContent>
            <Box>
              <Box sx={{ width: "100%" }}>
                {/* Start Date */}
                <Box sx={{ marginTop: "12px" }}>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      sx={{ marginRight: "10px", marginBottom: "15px" }}
                      format="MM/DD/YYYY"
                      label="Date"
                      value={formik.values.date}
                      onChange={(newValue) => {
                        let date = dayjs(newValue)
                          .tz(DEFAULT_TIMEZONE)
                          .startOf("day");
                        let startDate = date.startOf("day").add(7, "hours");
                        let endDate = date.startOf("day").add(8, "hours");

                        formik.setValues((prev) => ({
                          ...prev,
                          date,
                          startDate,
                          endDate,
                        }));
                      }}
                      renderInput={(params) => (
                        <TextField
                          size="small"
                          fullWidth
                          sx={{}}
                          onBlur={formik.handleBlur}
                          error={
                            formik.touched.date && Boolean(formik.errors.date)
                          }
                          helperText={
                            formik.errors.date && `${formik.errors.date}`
                          }
                          onFocus={(e) => {
                            e.target.select();
                          }}
                          {...params}
                        />
                      )}
                    />
                    {formik.values.date && formik.values.date.isValid() && (
                      <Box sx={{ display: "flex", flexDirection: "row" }}>
                        <DesktopTimePicker
                          label="From"
                          value={formik.values.startDate}
                          onChange={(newValue) => {
                            formik.setValues((prev) => ({
                              ...prev,
                              startDate: dayjs(newValue).tz(DEFAULT_TIMEZONE),
                            }));
                          }}
                          renderInput={(params) => (
                            <TextField
                              size="small"
                              fullWidth
                              sx={{ marginTop: "8px", marginRight: "8px" }}
                              onBlur={formik.handleBlur}
                              error={
                                formik.touched.startDate &&
                                Boolean(formik.errors.startDate)
                              }
                              helperText={
                                formik.errors.startDate &&
                                `${formik.errors.startDate}`
                              }
                              onFocus={(e) => {
                                e.target.select();
                              }}
                              {...params}
                            />
                          )}
                          // maxTime={formik.values.endDate}
                        />
                        <DesktopTimePicker
                          label="To"
                          value={formik.values.endDate}
                          onChange={(newValue) => {
                            formik.setValues((prev) => ({
                              ...prev,
                              endDate: dayjs(newValue).tz(DEFAULT_TIMEZONE),
                            }));
                          }}
                          renderInput={(params) => (
                            <TextField
                              size="small"
                              fullWidth
                              sx={{ marginTop: "8px", marginLeft: "8px" }}
                              onBlur={formik.handleBlur}
                              error={
                                formik.touched.endDate &&
                                Boolean(formik.errors.endDate)
                              }
                              helperText={
                                formik.errors.endDate &&
                                `${formik.errors.endDate}`
                              }
                              onFocus={(e) => {
                                e.target.select();
                              }}
                              {...params}
                            />
                          )}
                          // minTime={formik.values.endDate}
                        />
                      </Box>
                    )}
                  </LocalizationProvider>
                </Box>
              </Box>
              {/* Employee Select */}
              <Box sx={{ marginTop: "12px" }}>
                <SelectMultipleShopEmployees
                  size="small"
                  fullWidth
                  label="Assign To"
                  value={formik.values.assignedTo}
                  onUpdate={(users) => {
                    formik.setFieldValue("assignedTo", users);
                  }}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.assignedTo &&
                    Boolean(formik.errors.assignedTo)
                  }
                  helperText={
                    formik.errors.assignedTo && `${formik.errors.assignedTo}`
                  }
                  onFocus={(e) => {
                    e.target.select();
                  }}
                />
              </Box>
              {/* Work Station Select */}
              <Box sx={{ marginTop: "12px" }}>
                <SelectWorkStation
                  size="small"
                  fullWidth
                  label="Work Station"
                  value={formik.values.workStation}
                  onUpdate={(workStation) => {
                    formik.setFieldValue("workStation", workStation);
                  }}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.assignedTo &&
                    Boolean(formik.errors.assignedTo)
                  }
                  helperText={
                    formik.errors.assignedTo && `${formik.errors.assignedTo}`
                  }
                  onFocus={(e) => {
                    e.target.select();
                  }}
                />
              </Box>

              <Divider sx={{ marginY: "10px" }} />

              <Typography variant="h6">Tasks</Typography>
              <TextField
                size="small"
                sx={{ marginTop: "16px" }}
                select
                variant="outlined"
                label="Schedule Job For"
                id="eventEntity"
                name="eventEntity"
                fullWidth
                value={formik.values.eventEntity}
                onChange={(e) => {
                  formik.setValues({
                    ...formik.values,
                    eventEntity: e.target.value,
                    order: null,
                    manufacturingOrderId: null,
                    subPhases: [],
                  });
                }}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.eventEntity &&
                  Boolean(formik.errors.eventEntity)
                }
                helperText={
                  formik.errors.eventEntity && `${formik.errors.eventEntity}`
                }
              >
                <MenuItem value={"IND"}>Industry</MenuItem>
                <MenuItem value={"RFL"}>RFL</MenuItem>
              </TextField>
              {/* Jobs - select */}
              {formik.values.eventEntity == "IND" && (
                <Box sx={{ marginTop: "12px" }}>
                  <SelectEventOrder
                    size="small"
                    value={formik.values.order}
                    onUpdate={(order) => {
                      formik.setValues((prev) => ({
                        ...prev,
                        order,
                        subPhases: [],
                      }));
                    }}
                    onBlur={formik.handleBlur}
                    error={formik.touched.order && Boolean(formik.errors.order)}
                    helperText={formik.errors.order && `${formik.errors.order}`}
                    onFocus={(e) => {
                      e.target.select();
                    }}
                  />
                  {/* Phases */}
                  {formik.values.order && (
                    <SelectEventPhases
                      jobId={formik.values.order.id}
                      scheduledSubPhases={formik.values.subPhases}
                      onUpdate={(subPhases) => {
                        formik.setFieldValue("subPhases", subPhases);
                      }}
                      onBlur={formik.handleBlur}
                      error={
                        formik.touched.subPhases &&
                        Boolean(formik.errors.subPhases)
                      }
                      helperText={
                        formik.errors.subPhases && `${formik.errors.subPhases}`
                      }
                      onFocus={(e) => {
                        e.target.select();
                      }}
                    />
                  )}
                </Box>
              )}

              {formik.values.eventEntity == "RFL" && (
                <Box sx={{ marginTop: "12px" }}>
                  <SelectManufacturingOrder
                    value={
                      formik.values.manufacturingOrderId
                        ? { id: formik.values.manufacturingOrderId }
                        : null
                    }
                    onUpdate={(mfgOrder) => {
                      console.log("onUpdate", mfgOrder);
                      formik.setFieldValue("manufacturingOrderId", mfgOrder.id);
                    }}
                    onBlur={formik.handleBlur}
                    error={
                      formik.touched.manufacturingOrderId &&
                      Boolean(formik.errors.manufacturingOrderId)
                    }
                    helperText={
                      formik.errors.manufacturingOrderId &&
                      `${formik.errors.manufacturingOrderId}`
                    }
                    onFocus={(e) => {
                      e.target.select();
                    }}
                  />
                  {/* RFL Work Order Tasks */}
                  {formik.values.manufacturingOrderId && (
                    <SelectEventRFLTasks
                      orderId={formik.values.manufacturingOrderId}
                      scheduledRFLTasks={formik.values.rflTasks}
                      onUpdate={(tasks) => {
                        formik.setFieldValue("rflTasks", tasks);
                      }}
                      onBlur={formik.handleBlur}
                      error={
                        formik.touched.subPhases &&
                        Boolean(formik.errors.subPhases)
                      }
                      helperText={
                        formik.errors.subPhases && `${formik.errors.subPhases}`
                      }
                      onFocus={(e) => {
                        e.target.select();
                      }}
                    />
                  )}
                </Box>
              )}

              {errorMessage ? (
                <Typography sx={{ color: "#d32f2f", marginTop: "5px" }}>
                  {errorMessage}
                </Typography>
              ) : (
                <></>
              )}
            </Box>
          </DialogContent>
        )}

        <DialogActions>
          <Button
            onClick={() => {
              formik.handleSubmit();
            }}
          >
            Submit
          </Button>
        </DialogActions>
      </Dialog>
    );
};

export default ScheduleJobDialog;
